import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { isOwnerOrAdmin, type SidebarNavVisibilityContext } from "@/config/sidebar-nav";
import { isViewAsRole, type ViewAsRole } from "@/config/view-as";
import { useProfile } from "@/context/ProfileContext";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import { usePulseCheckInEnabled } from "@/hooks/use-pulse-check-in-enabled";
import { useTeamScope } from "@/context/TeamScopeContext";
import { useSalesTeams } from "@/hooks/queries/use-sales-teams";
import { useIsDvhBusiness } from "@/hooks/use-business-vertical";

export type { ViewAsRole } from "@/config/view-as";

/** localStorage key prefix for the preview role, scoped per business. */
const STORAGE_PREFIX = "graftus_viewAs_";

interface ViewAsContextValue {
  /** The role the dashboard renders as: the preview when one is set, else the real one. */
  effectiveRole: ViewAsRole;
  /** The role the membership maps to, regardless of any preview. */
  realRole: ViewAsRole;
  /** The chosen preview, or null when the real role is shown. */
  viewAs: ViewAsRole | null;
  /** True for owners and admins, the only ones who may preview another role. */
  canPreview: boolean;
  setViewAs: (role: ViewAsRole | null) => void;
}

const ViewAsContext = createContext<ViewAsContextValue | undefined>(undefined);

function readStored(businessId: string): ViewAsRole | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + businessId);
    return isViewAsRole(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * The "Visa som" state of the platform sketch (Oct 2026). Owners and admins
 * see the whole dashboard and may preview it as a department head or an
 * employee; the preview is remembered per business and only ever applies
 * to them. A team-scoped member is a department head, everyone else an
 * employee, and neither can switch. Switching to the head view seeds the
 * active team with the first department so the view has something to show.
 * Sits inside `TeamScopeProvider` and `ProfileProvider`.
 */
export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { businessProfile, memberAccessLevel, memberRole, memberPermissions } = useProfile();
  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  const showPulseCheck = usePulseCheckInEnabled();
  const businessId = businessProfile?.uid ?? "";
  const { activeTeamId, setActiveTeamId } = useTeamScope();
  const isDvh = useIsDvhBusiness();

  // The same visibility context the sidebar resolves, so "may preview" and
  // "sees Data and Behörighet" never disagree.
  const navCtx = useMemo<SidebarNavVisibilityContext>(
    () => ({
      businessProfile,
      remoteConfig: { showIntelligence, showPulseCheck },
      memberAccessLevel,
      memberRole,
      memberPermissions,
    }),
    [businessProfile, showIntelligence, showPulseCheck, memberAccessLevel, memberRole, memberPermissions],
  );
  const canPreview = isOwnerOrAdmin(navCtx);
  const realRole: ViewAsRole = canPreview
    ? "owner"
    : memberAccessLevel === "team"
      ? "head"
      : "employee";

  const [stored, setStored] = useState<ViewAsRole | null>(null);
  const [hydratedBusinessId, setHydratedBusinessId] = useState<string | null>(null);

  useEffect(() => {
    if (!businessId) {
      setStored(null);
      setHydratedBusinessId(null);
      return;
    }
    if (hydratedBusinessId === businessId) return;
    setStored(readStored(businessId));
    setHydratedBusinessId(businessId);
  }, [businessId, hydratedBusinessId]);

  const viewAs = canPreview && stored && stored !== "owner" ? stored : null;
  const effectiveRole: ViewAsRole = viewAs ?? realRole;

  // Only needed to seed the head preview; report-only accounts never get here.
  const teamsQuery = useSalesTeams(
    canPreview && effectiveRole === "head" && businessId ? businessId : undefined,
  );
  const firstTeamId = useMemo(() => {
    const teams = (teamsQuery.data ?? []) as Array<{ id: string; parentTeamId?: string | null }>;
    const departments = isDvh ? teams.filter((t) => t.parentTeamId) : [];
    return (departments.length > 0 ? departments : teams)[0]?.id ?? null;
  }, [teamsQuery.data, isDvh]);

  useEffect(() => {
    if (!canPreview || effectiveRole !== "head") return;
    if (activeTeamId !== null || !firstTeamId) return;
    setActiveTeamId(firstTeamId);
  }, [canPreview, effectiveRole, activeTeamId, firstTeamId, setActiveTeamId]);

  const setViewAs = useCallback(
    (role: ViewAsRole | null) => {
      const next = role === "owner" ? null : role;
      setStored(next);
      if (!businessId) return;
      try {
        if (next) localStorage.setItem(STORAGE_PREFIX + businessId, next);
        else localStorage.removeItem(STORAGE_PREFIX + businessId);
      } catch {
        // Private mode or blocked storage: the choice lives for the session.
      }
    },
    [businessId],
  );

  const value = useMemo<ViewAsContextValue>(
    () => ({ effectiveRole, realRole, viewAs, canPreview, setViewAs }),
    [effectiveRole, realRole, viewAs, canPreview, setViewAs],
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

/**
 * The preview state. Outside the provider (older tests, storybook-like
 * renders) it reports the real role with no preview, so a page can read it
 * without caring whether the shell mounted the provider.
 */
export function useViewAs(): ViewAsContextValue {
  const ctx = useContext(ViewAsContext);
  if (ctx) return ctx;
  return FALLBACK;
}

const FALLBACK: ViewAsContextValue = {
  effectiveRole: "owner",
  realRole: "owner",
  viewAs: null,
  canPreview: false,
  setViewAs: () => {},
};
